import React from 'react'
import { useTranslation } from 'react-i18next'

import Dialog from '@mui/material/Dialog'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'
import LoadingButton from '@mui/lab/LoadingButton'

import { patch } from '../../utils/service'
import Alert from '../alert/alert'
import { Log } from '../../types/log'
import NumberField from '../fields/numberField'
import { getFullscreenElement } from '../../utils/graphUtils'
import { get } from '../../utils/service'
import { Inventory } from '../../types/inventory'

interface BatchColorDialogProps {
  log: Log
  onClose: () => void
  onSaveSuccess: (newLogData: Log) => void
}

const BatchColorDialog = (props: BatchColorDialogProps): JSX.Element => {
  const { t } = useTranslation()

  const [wholeBeanColor, setWholeBeanColor] = React.useState<number>(props.log.whole_bean_color || 0)
  const [groundBeanColor, setGroundBeanColor] = React.useState<number>(props.log.ground_bean_color || 0)
  const [greenBeanColor, setGreenBeanColor] = React.useState<number>(props.log.green_bean_color || 0)
  const [isSaving, setIsSaving] = React.useState<boolean>(false)
  const [hasSaveError, setHasSaveError] = React.useState<boolean>(false)

  const getGreenColor = React.useCallback(() => {
    if (props.log.inventory_id) {
      const url = `inventories/${props.log.inventory_id}/`
      get<Inventory>(url).then((response) => {
        setGreenBeanColor(response.data.green_bean_color || 0)
      })
    }
    return 0
  }, [props.log.inventory_id])

  React.useEffect(() => {
    if (!props.log.green_bean_color) {
      getGreenColor()
    }
  }, [props.log.green_bean_color, getGreenColor])

  const saveColors = (newWholeBeanColor: number, newGroundBeanColor: number, newGreenBeanColor: number): void => {
    setIsSaving(true)
    patch<
      {
        whole_bean_color: number | null
        ground_bean_color: number | null
        green_bean_color: number | null
      },
      Log
    >(props.log.url, {
      whole_bean_color: newWholeBeanColor || null,
      ground_bean_color: newGroundBeanColor || null,
      green_bean_color: newGreenBeanColor || null,
    })
      .then((response) => {
        props.onSaveSuccess(response.data)
      })
      .catch(() => {
        setHasSaveError(true)
        setIsSaving(false)
      })
  }

  return (
    <Dialog open={true} onClose={props.onClose} container={getFullscreenElement() || document.body}>
      <DialogContent>
        <Stack spacing={2}>
          <Stack sx={{ width: 135 }}>
            <NumberField
              isSmall
              autoFocus={true}
              valueSuffix=""
              numValue={wholeBeanColor}
              label={t('logview.wholebeancolor')}
              minNumValue={0}
              maxNumValue={1000}
              decimalPlaces={1}
              onBlur={(newValue: number) => setWholeBeanColor(newValue)}
              onEnter={(newValue: number) => saveColors(newValue, groundBeanColor, greenBeanColor)}
            />
          </Stack>
          <Stack sx={{ width: 135 }}>
            <NumberField
              isSmall
              valueSuffix=""
              numValue={groundBeanColor}
              label={t('logview.groundbeancolor')}
              minNumValue={0}
              maxNumValue={1000}
              decimalPlaces={1}
              onBlur={(newValue: number) => setGroundBeanColor(newValue)}
              onEnter={(newValue: number) => saveColors(wholeBeanColor, newValue, greenBeanColor)}
            />
          </Stack>
          <Stack sx={{ width: 135 }}>
            <NumberField
              isSmall
              valueSuffix=""
              numValue={greenBeanColor}
              label={t('logview.greenbeancolor')}
              minNumValue={0}
              maxNumValue={1000}
              decimalPlaces={1}
              onBlur={(newValue: number) => setGreenBeanColor(newValue)}
              onEnter={(newValue: number) => saveColors(wholeBeanColor, groundBeanColor, newValue)}
            />
          </Stack>
          {hasSaveError && <Alert severity="error">{t('logview.setcolorerror')}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={props.onClose}>{t('common.cancel')}</Button>
        <LoadingButton
          variant="contained"
          loading={isSaving}
          onClick={() => saveColors(wholeBeanColor, groundBeanColor, greenBeanColor)}
        >
          {t('logview.setcolor')}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  )
}

export default BatchColorDialog
